import * as React from 'react'

interface PublishedPageSize {
	pageSize: number
	disabled: boolean
}

interface PageSizeScopeValue {
	published: PublishedPageSize | null
	setPublished: (value: PublishedPageSize | null) => void
	setDispatch: (handler: ((pageSize: number) => void) | null) => void
	dispatch: (pageSize: number) => void
	hasDisplay: boolean
	setHasDisplay: (value: boolean) => void
}

const PageSizeScopeContext = React.createContext<PageSizeScopeValue | null>(null)

/**
 * Lets a `Pagination` hand its page size to a display popover rendered beside it, so the setting
 * lives under Display instead of taking permanent room in the toolbar. One scope is one toolbar:
 * the pager publishes, the popover claims. A pager with no popover in scope keeps its own dropdown,
 * which is why the two register each other rather than the components simply assuming.
 */
export function PageSizeScope({ children }: { children: React.ReactNode }) {
	const [published, setPublished] = React.useState<PublishedPageSize | null>(null)
	const [hasDisplay, setHasDisplay] = React.useState(false)
	// The handler lives in a ref so a caller passing an inline arrow doesn't republish every render.
	const dispatchRef = React.useRef<((pageSize: number) => void) | null>(null)

	const setDispatch = React.useCallback((handler: ((pageSize: number) => void) | null) => {
		dispatchRef.current = handler
	}, [])
	const dispatch = React.useCallback((pageSize: number) => dispatchRef.current?.(pageSize), [])

	const value = React.useMemo(
		() => ({ published, setPublished, setDispatch, dispatch, hasDisplay, setHasDisplay }),
		[published, hasDisplay, dispatch, setDispatch],
	)

	return <PageSizeScopeContext.Provider value={value}>{children}</PageSizeScopeContext.Provider>
}

/**
 * Publishes the pager's page size to the surrounding scope. Returns true once a display popover in
 * the same scope has claimed it, which is the pager's cue to drop its own dropdown.
 */
export function usePublishPageSize(pageSize: number, disabled: boolean, onPageSizeChange: (size: number) => void) {
	const scope = React.useContext(PageSizeScopeContext)
	const { setPublished, setDispatch, hasDisplay } = scope ?? {}

	const handlerRef = React.useRef(onPageSizeChange)
	handlerRef.current = onPageSizeChange

	React.useEffect(() => {
		if (!setPublished || !setDispatch) {
			return
		}
		setDispatch(size => handlerRef.current(size))
		setPublished({ pageSize, disabled })
		return () => {
			setDispatch(null)
			setPublished(null)
		}
	}, [setPublished, setDispatch, pageSize, disabled])

	return hasDisplay ?? false
}

/**
 * Claims the scope's page size for a display popover. Null when no pager published one. Pass false
 * for `enabled` when the popover renders nothing of its own: it then leaves the page size with the
 * pager rather than hiding it inside a popover that never opens.
 */
export function useScopedPageSize(enabled: boolean) {
	const scope = React.useContext(PageSizeScopeContext)
	const setHasDisplay = scope?.setHasDisplay

	React.useEffect(() => {
		if (!setHasDisplay || !enabled) {
			return
		}
		setHasDisplay(true)
		return () => setHasDisplay(false)
	}, [setHasDisplay, enabled])

	if (!enabled || !scope?.published) {
		return null
	}
	return { ...scope.published, onPageSizeChange: scope.dispatch }
}
